"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  createSortableColumn,
  DataTable,
  Input,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type TanstackReactTable,
  Textarea,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  AlertTriangleIcon,
  CheckCircleIcon,
  CircleMinusIcon,
  CrownIcon,
  EyeIcon,
  FlameIcon,
  ShieldAlertIcon,
  ShieldCheckIcon,
  TriangleAlertIcon,
  UserCheckIcon,
  UserIcon,
  XIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type {
  KycTierLimitItem,
  SuspiciousFlagItem,
} from "../api/compliance-client";
import {
  useKycLimitsQuery,
  useKycWalletCountsQuery,
  useReviewFlagMutation,
  useSuspiciousListQuery,
  useSuspiciousStatsQuery,
  useUpdateKycLimitMutation,
} from "../api/compliance-queries";

// --- Constants ---

const TIER_LABELS: Record<string, string> = {
  unverified: "ບໍ່ໄດ້ຢືນຢັນ",
  basic: "ພື້ນຖານ",
  verified: "ຢືນຢັນແລ້ວ",
  premium: "ພຣີມຽມ",
};

const TIER_COLORS: Record<
  string,
  "default" | "secondary" | "outline" | "destructive"
> = {
  unverified: "destructive",
  basic: "secondary",
  verified: "default",
  premium: "outline",
};

const TIER_ICONS: Record<
  string,
  { icon: typeof UserIcon; bg: string; fg: string }
> = {
  unverified: {
    icon: UserIcon,
    bg: "bg-destructive/10",
    fg: "text-destructive",
  },
  basic: {
    icon: UserCheckIcon,
    bg: "bg-secondary",
    fg: "text-secondary-foreground",
  },
  verified: { icon: ShieldCheckIcon, bg: "bg-primary/10", fg: "text-primary" },
  premium: {
    icon: CrownIcon,
    bg: "bg-amber-100 dark:bg-amber-500/10",
    fg: "text-amber-600 dark:text-amber-400",
  },
};

const SEVERITY_STYLE: Record<
  string,
  { icon: typeof UserIcon; color: string; label: string }
> = {
  low: { icon: CircleMinusIcon, color: "text-muted-foreground", label: "ຕ່ຳ" },
  medium: { icon: ShieldAlertIcon, color: "text-primary", label: "ປານກາງ" },
  high: { icon: TriangleAlertIcon, color: "text-orange-500", label: "ສູງ" },
  critical: { icon: FlameIcon, color: "text-destructive", label: "ວິກິດ" },
};

const FLAG_STATUS_STYLE: Record<
  string,
  {
    variant: "default" | "secondary" | "outline" | "destructive";
    label: string;
  }
> = {
  pending: { variant: "destructive", label: "ລໍຖ້າ" },
  reviewed: { variant: "secondary", label: "ກວດແລ້ວ" },
  escalated: { variant: "default", label: "ຍົກລະດັບ" },
  cleared: { variant: "outline", label: "ປອດໄພ" },
};

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "pending", label: "ລໍຖ້າ" },
  { value: "reviewed", label: "ກວດແລ້ວ" },
  { value: "escalated", label: "ຍົກລະດັບ" },
  { value: "cleared", label: "ປອດໄພ" },
] as const;

const SEVERITY_OPTIONS = [
  { value: "all", label: "ຄວາມຮ້າຍແຮງທັງໝົດ" },
  { value: "low", label: "ຕ່ຳ" },
  { value: "medium", label: "ປານກາງ" },
  { value: "high", label: "ສູງ" },
  { value: "critical", label: "ວິກິດ" },
] as const;

// --- Main Page ---

export function CompliancePage() {
  const search = useSearch({ from: "/app/wallet/compliance" }) as {
    tab: "kyc" | "suspicious";
  };
  const nav = useNavigate({ from: "/app/wallet/compliance" });

  const tab = search.tab ?? "kyc";

  function handleTabChange(value: "kyc" | "suspicious") {
    nav({
      search: {
        ...search,
        tab: value,
      },
    });
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄວາມສອດຄ່ອງ & ຄວາມສ່ຽງ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex items-center justify-between">
          <div className="">
            <h2 className="font-bold text-2xl tracking-tight">
              ຄວາມສອດຄ່ອງ & ຄວາມສ່ຽງ
            </h2>
            <p className="text-muted-foreground">
              ກຳນົດລິມິດ KYC ແລະ ກວດສອບທຸລະກຳສົງໄສ
            </p>
          </div>

          <Tabs
            value={tab}
            onValueChange={(value) =>
              handleTabChange(value as "kyc" | "suspicious")
            }
            className="space-y-4"
          >
            <TabsList>
              <TabsTrigger value="kyc">ລິມິດ KYC</TabsTrigger>
              <TabsTrigger value="suspicious">ທຸລະກຳສົງໄສ</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <Tabs value={tab} className="space-y-4">
          <TabsContent value="kyc">
            <KycLimitsSection />
          </TabsContent>

          <TabsContent value="suspicious">
            <SuspiciousSection />
          </TabsContent>
        </Tabs>
      </Main>
    </>
  );
}

// --- KYC Limits Section ---

function KycLimitsSection() {
  const { data, isLoading } = useKycLimitsQuery();
  const { data: walletCounts } = useKycWalletCountsQuery();

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-[200px] rounded-xl" />
        ))}
      </div>
    );
  }

  const items = data?.data ?? [];

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          ຍັງບໍ່ມີການຕັ້ງຄ່າລິມິດ KYC
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      {items.map((item) => (
        <KycTierCard
          key={item.id}
          item={item}
          walletCount={walletCounts?.[item.tier] ?? 0}
        />
      ))}
    </div>
  );
}

function KycTierCard({
  item,
  walletCount,
}: {
  item: KycTierLimitItem;
  walletCount: number;
}) {
  const [editing, setEditing] = useState(false);
  const [dailyWithdraw, setDailyWithdraw] = useState(
    String(item.dailyWithdraw),
  );
  const [monthlyWithdraw, setMonthlyWithdraw] = useState(
    String(item.monthlyWithdraw),
  );
  const [dailyTopup, setDailyTopup] = useState(String(item.dailyTopup));
  const updateMutation = useUpdateKycLimitMutation(item.id);

  function handleSave() {
    updateMutation.mutate(
      {
        dailyWithdraw: Number(dailyWithdraw),
        monthlyWithdraw: Number(monthlyWithdraw),
        dailyTopup: Number(dailyTopup),
      },
      {
        onSuccess: () => setEditing(false),
      },
    );
  }

  function handleCancel() {
    setDailyWithdraw(String(item.dailyWithdraw));
    setMonthlyWithdraw(String(item.monthlyWithdraw));
    setDailyTopup(String(item.dailyTopup));
    setEditing(false);
  }

  const tierStyle = TIER_ICONS[item.tier] ?? {
    icon: UserIcon,
    bg: "bg-muted",
    fg: "text-muted-foreground",
  };
  const TierIcon = tierStyle.icon;

  return (
    <Card className="rounded-xl">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className={`flex size-10 items-center justify-center rounded-lg ${tierStyle.bg}`}
            >
              <TierIcon className={`size-5 ${tierStyle.fg}`} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">
                  {TIER_LABELS[item.tier] ?? item.tier}
                </CardTitle>
                <Badge variant={TIER_COLORS[item.tier] ?? "outline"}>
                  {item.tier}
                </Badge>
              </div>
            </div>
          </div>
          <div className="text-right">
            <span className="font-semibold text-sm tabular-nums">
              {walletCount}
            </span>
            <p className="text-muted-foreground text-xs">ກະເປົາ</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <LimitRow
          label="ຖອນເງິນ/ມື້"
          value={dailyWithdraw}
          currency={item.currency}
          editing={editing}
          onChange={setDailyWithdraw}
        />
        <LimitRow
          label="ຖອນເງິນ/ເດືອນ"
          value={monthlyWithdraw}
          currency={item.currency}
          editing={editing}
          onChange={setMonthlyWithdraw}
        />
        <LimitRow
          label="ເຕີມເງິນ/ມື້"
          value={dailyTopup}
          currency={item.currency}
          editing={editing}
          onChange={setDailyTopup}
        />

        {item.description && (
          <p className="text-muted-foreground text-xs">{item.description}</p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          {editing ? (
            <>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                ຍົກເລີກ
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                isLoading={updateMutation.isPending}
              >
                ບັນທຶກ
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
            >
              ແກ້ໄຂ
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function LimitRow({
  label,
  value,
  currency,
  editing,
  onChange,
}: {
  label: string;
  value: string;
  currency: string;
  editing: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground text-sm">{label}</span>
      {editing ? (
        <Input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-8 w-40 text-right"
        />
      ) : (
        <span className="font-semibold tabular-nums">
          {formatPrice(Number(value), currency)}
        </span>
      )}
    </div>
  );
}

// --- Suspicious Section ---

function SuspiciousSection() {
  return (
    <div className="space-y-4">
      <SuspiciousStatsCards />
      <SuspiciousTable />
    </div>
  );
}

function SuspiciousStatsCards() {
  const { data: stats, isLoading } = useSuspiciousStatsQuery();

  if (isLoading) {
    return (
      <div className="grid gap-4 sm:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <SummaryCard
        title="ລໍຖ້າກວດສອບ"
        icon={ShieldAlertIcon}
        color={stats.pending > 0 ? "orange" : "muted"}
        items={[{ label: "", value: stats.pending.toLocaleString() }]}
        subtitle="ລາຍການທີ່ຍັງບໍ່ໄດ້ review"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ຄວາມຮ້າຍແຮງສູງ/ວິກິດ"
        icon={AlertTriangleIcon}
        color={stats.high > 0 ? "destructive" : "muted"}
        items={[{ label: "", value: stats.high.toLocaleString() }]}
        subtitle="high + critical ທີ່ຍັງ pending"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ກວດແລ້ວ (ມື້ນີ້)"
        icon={CheckCircleIcon}
        color="success"
        items={[{ label: "", value: stats.reviewedToday.toLocaleString() }]}
        subtitle="ລາຍການທີ່ reviewed ມື້ນີ້"
        subtitleClassName="text-muted-foreground"
      />
    </div>
  );
}

function SuspiciousTable() {
  const nav = useNavigate({ from: "/app/wallet/compliance" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/compliance",
  }) as OffsetPageQueryDTO;

  const { data, isLoading } = useSuspiciousListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const statusFilter =
    (findCondition(filters, "status")?.value as string) || "all";
  const severityFilter =
    (findCondition(filters, "severity")?.value as string) || "all";
  const hasFilter = statusFilter !== "all" || severityFilter !== "all";

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, { field, op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<SuspiciousFlagItem>[] = [
    createSortableColumn<SuspiciousFlagItem>("createdAt", "ວັນທີ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "severity",
      header: "ຄວາມຮ້າຍແຮງ",
      cell: ({ row }) => {
        const s = SEVERITY_STYLE[row.original.severity] ?? {
          icon: CircleMinusIcon,
          color: "text-muted-foreground",
          label: row.original.severity,
        };
        const SevIcon = s.icon;
        return (
          <div className="flex items-center gap-2">
            <SevIcon className={`size-4 ${s.color}`} />
            <span className="text-sm">{s.label}</span>
          </div>
        );
      },
      size: 120,
    },
    {
      id: "reason",
      header: "ເຫດຜົນ",
      cell: ({ row }) => (
        <span className="line-clamp-2 text-sm">{row.original.reason}</span>
      ),
      size: 250,
    },
    {
      id: "wallet",
      header: "ກະເປົາ",
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          {row.original.walletId.slice(0, 8)}...
        </span>
      ),
      size: 100,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = FLAG_STATUS_STYLE[row.original.status] ?? {
          variant: "outline" as const,
          label: row.original.status,
        };
        return <Badge variant={s.variant}>{s.label}</Badge>;
      },
      size: 100,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        row.original.status === "pending" ? (
          <ReviewActions flag={row.original} />
        ) : (
          <span className="text-muted-foreground text-xs">
            {row.original.reviewedByName ?? "—"}
          </span>
        ),
      size: 140,
    },
  ];

  return (
    <div className="flex flex-col rounded-xl border bg-card pt-2">
      <div className="mb-4 flex flex-col gap-2 px-2">
        <div className="flex items-center justify-between gap-4 max-sm:flex-col">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <SimpleSelect
              value={statusFilter}
              onValueChange={(v) => handleFilterChange("status", v)}
              placeholder="ສະຖານະ"
              className="h-8 w-full sm:w-40"
              options={STATUS_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
            <SimpleSelect
              value={severityFilter}
              onValueChange={(v) => handleFilterChange("severity", v)}
              placeholder="ຄວາມຮ້າຍແຮງ"
              className="h-8 w-full sm:w-48"
              options={SEVERITY_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
              }))}
            />
          </div>
          {hasFilter && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                nav({
                  search: { ...search, filters: undefined, offset: 0 },
                })
              }
              className="max-sm:w-full"
            >
              <XIcon className="size-4" /> ລ້າງ
            </Button>
          )}
        </div>
      </div>

      <DataTable<SuspiciousFlagItem, unknown>
        noDataMessage="ບໍ່ພົບລາຍການສົງໄສ"
        isLoading={isLoading}
        columns={columns}
        data={data?.data ?? []}
        offset={search.offset ?? 0}
        limit={search.limit ?? 20}
        totalCount={data?.meta?.total ?? 0}
        onPaginationChange={(p) =>
          nav({ search: { ...search, offset: p.offset, limit: p.limit } })
        }
        sortBy={search.sort ? search.sort[0]?.field : undefined}
        sortOrder={
          search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
        }
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          nav({
            search: {
              ...search,
              sort: [
                {
                  field: sorting[0]?.id as string,
                  dir: sorting[0]?.desc ? "desc" : "asc",
                },
              ],
            },
          });
        }}
      />
    </div>
  );
}

function ReviewActions({ flag }: { flag: SuspiciousFlagItem }) {
  const reviewMutation = useReviewFlagMutation();
  const [note, setNote] = useState("");

  return (
    <div className="flex gap-1">
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm">
            <EyeIcon className="size-3" /> Review
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ກວດສອບລາຍການສົງໄສ</AlertDialogTitle>
            <AlertDialogDescription>
              ເຫດຜົນ: {flag.reason}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea
            placeholder="ບັນທຶກ (ບໍ່ບັງຄັບ)..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-2"
          />
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
            <AlertDialogAction
              className="bg-success hover:bg-success/90"
              onClick={() =>
                reviewMutation.mutate({
                  id: flag.id,
                  body: { action: "cleared", reviewNote: note || undefined },
                })
              }
            >
              ປອດໄພ
            </AlertDialogAction>
            <AlertDialogAction
              onClick={() =>
                reviewMutation.mutate({
                  id: flag.id,
                  body: { action: "reviewed", reviewNote: note || undefined },
                })
              }
            >
              ກວດແລ້ວ
            </AlertDialogAction>
            <AlertDialogAction
              className="bg-destructive hover:bg-destructive/90"
              onClick={() =>
                reviewMutation.mutate({
                  id: flag.id,
                  body: { action: "escalated", reviewNote: note || undefined },
                })
              }
            >
              ຍົກລະດັບ
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
